import ChatIcon from "@mui/icons-material/Chat";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`step-tabpanel-${index}`}
      aria-labelledby={`step-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export interface StepData {
  id: number;
  title: string;
  description: string;
  type: string;
  content: string;
  delay: number;
  delayUnit: string;
  condition?: string;
  subject?: string;
}

interface TemplateStepEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (step: StepData) => void;
  step: StepData | null;
}

const TemplateStepEditor: React.FC<TemplateStepEditorProps> = ({ open, onClose, onSave, step }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [stepData, setStepData] = useState<StepData>(
    step || {
      id: Date.now(),
      title: "",
      description: "",
      type: "email",
      content: "",
      delay: 1,
      delayUnit: "days",
    }
  );

  useEffect(() => {
    if (step) {
      setStepData(step);
      setTabValue(0); // Reset to first tab when opening a different step
    } else if (open) {
      // Reset form when opening to create a new step
      setStepData({
        id: Date.now(),
        title: "",
        description: "",
        type: "email",
        content: "",
        delay: 1,
        delayUnit: "days",
      });
      setTabValue(0);
    }
  }, [step, open]);

  const selectMenuProps = {
    PaperProps: {
      style: {
        zIndex: 9999,
      },
    },
  };
  const { t } = useTranslation();
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setStepData({
      ...stepData,
      [name]: value,
    });
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setStepData({
      ...stepData,
      [name]: value,
    });
  };

  const handleSave = () => {
    onSave(stepData);
    onClose();
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case "email":
        return <EmailIcon />;
      case "whatsapp":
        return <WhatsAppIcon />;
      case "sms":
        return <ChatIcon />;
      case "call":
        return <PhoneIcon />;
      default:
        return <EmailIcon />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 1000,
        "& .MuiDialog-paper": {
          overflow: "visible",
        },
      }}
    >
      <DialogTitle>{step ? "Modifica il passaggio" : "Aggiungi un nuovo passaggi"}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="step editor tabs">
            <Tab label="Informazioni di base" />
            <Tab label="Contenuto messaggio" />
            <Tab label="Temporizzazione" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titolo del passaggio"
                name="title"
                value={stepData.title}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrizione"
                name="description"
                value={stepData.description}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="type-label">Tipo di messaggio</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={stepData.type}
                  onChange={handleSelectChange}
                  label="Message Type"
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="call">Call Script</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {getIconForType(stepData.type)}
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                {stepData.type === "email"
                  ? "Contenuto email"
                  : stepData.type === "whatsapp"
                  ? "Messaggio WhatsApp"
                  : stepData.type === "sms"
                  ? "SMS"
                  : "Call Script"}
              </Typography>
            </Box>
            <Chip label={`Format: ${stepData.type.toUpperCase()}`} size="small" sx={{ mb: 2 }} />
          </Box>

          {stepData.type === "email" ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Oggetto"
                  name="subject"
                  value={stepData.subject || ""}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contenuto email"
                  name="content"
                  value={stepData.content}
                  onChange={handleChange}
                  variant="outlined"
                  multiline
                  rows={12}
                  placeholder="Scrivi il tuo contenuto email. Puoi usare {{firstName}} per personalizzare."
                />
              </Grid>
            </Grid>
          ) : stepData.type === "whatsapp" || stepData.type === "sms" ? (
            <TextField
              fullWidth
              label="Testo del messaggio"
              name="content"
              value={stepData.content}
              onChange={handleChange}
              variant="outlined"
              multiline
              rows={6}
              placeholder="Scrivi il tuo messaggio."
            />
          ) : (
            <TextField
              fullWidth
              label="Call Script"
              name="content"
              value={stepData.content}
              onChange={handleChange}
              variant="outlined"
              multiline
              rows={10}
              placeholder="Scrivi lo script della chiamata. Includi tono e punti forti."
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ritardo"
                name="delay"
                type="number"
                value={stepData.delay}
                onChange={handleChange}
                variant="outlined"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="delay-unit-label">Unità di tempo</InputLabel>
                <Select
                  labelId="delay-unit-label"
                  id="delayUnit"
                  name="delayUnit"
                  value={stepData.delayUnit}
                  onChange={handleSelectChange}
                  label="Unità di tempo"
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="minutes">Minuti</MenuItem>
                  <MenuItem value="hours">Ore</MenuItem>
                  <MenuItem value="days">Giorni</MenuItem>
                  <MenuItem value="weeks">Settimane</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Annulla
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {step ? "Aggiorna passaggio" : "Aggiungi passaggio"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateStepEditor;
